import 'maplibre-gl/dist/maplibre-gl.css';
import type { api } from '@udm/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, MapPin, Plus, Upload } from 'lucide-react';
import {
  AttributionControl,
  Map as MapLibreMap,
  NavigationControl,
  type GeoJSONSource,
  type GeoJSONSourceSpecification,
} from 'maplibre-gl';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { canAny } from '../../api/client';
import { fetchAssetTypes, fetchAssets, importAssets } from '../../api/ops';
import { usePrefs } from '../../app/prefs';
import { deviceStatic } from '../../realtime/deviceStatic';
import { Card, Field, Modal, PageHeader } from '../../ui/components';
import { useMe } from '../auth/session';
import { baseStyle } from '../map/baseStyle';
import { addOverlayLayers, assetsData, ASSETS_SOURCE, setOverlay } from '../map/overlays';
import { ReportScopePicker, useReportScope } from '../reports/ReportScope';
import { AssetDrawer, type Draft } from './AssetDrawer';

const DRAW = 'asset-draw';

/**
 * GIS assets (AFM requirement f): poles, cabinets, transformer stations, festive sockets, parking, water / sewer and
 * gas elements, cable lines … on the map with the devices, with properties, relations, maintenance notes and documents.
 * Geometry: a click places a point; lines and polygons are drawn vertex by vertex.
 */
export function AssetsPage() {
  const { t } = useTranslation();
  const me = useMe().data;
  const qc = useQueryClient();
  const theme = usePrefs((s) => s.theme);
  const { clientId } = useReportScope();
  const [type, setType] = useState('');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [importing, setImporting] = useState(false);
  const types = useQuery({ queryKey: ['asset-types'], queryFn: fetchAssetTypes, staleTime: 600_000 });
  const assets = useQuery({
    queryKey: ['assets', 'page', clientId, type, q],
    queryFn: () => fetchAssets({ client_id: clientId, type: type || undefined, q: q || undefined }),
    enabled: !!clientId,
  });
  const manage = canAny(me, 'asset.manage');
  const ref = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const draftRef = useRef<Draft | null>(null);
  draftRef.current = draft;
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    const map = new MapLibreMap({
      container: ref.current,
      style: baseStyle(theme),
      center: [27.1, 47.3],
      zoom: 9,
      attributionControl: false,
    });
    map.addControl(new NavigationControl({ showCompass: false }), 'bottom-right');
    map.addControl(new AttributionControl({ compact: false }), 'bottom-right');
    mapRef.current = map;
    map.once('style.load', () => {
      // devices as small reference dots (static registry; no realtime needed here)
      map.addSource('asset-devices', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [...deviceStatic.values()]
            .filter((d) => d.lon !== undefined && d.lat !== undefined)
            .map((d) => ({
              type: 'Feature',
              geometry: { type: 'Point', coordinates: [d.lon!, d.lat!] },
              properties: { id: d.id },
            })),
        } as GeoJSONSourceSpecification['data'],
      });
      map.addLayer({
        id: 'asset-devices',
        type: 'circle',
        source: 'asset-devices',
        paint: { 'circle-radius': 2.5, 'circle-color': '#9aa1ab' },
      });
      addOverlayLayers(map);
      map.addSource(DRAW, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({
        id: 'draw-line',
        type: 'line',
        source: DRAW,
        paint: { 'line-color': '#1a6fd8', 'line-width': 2, 'line-dasharray': [2, 2] },
      });
      map.addLayer({
        id: 'draw-points',
        type: 'circle',
        source: DRAW,
        filter: ['==', ['geometry-type'], 'Point'],
        paint: { 'circle-radius': 5, 'circle-color': '#1a6fd8' },
      });
      setReady(true);
    });
    map.on('click', (e) => {
      const d = draftRef.current;
      if (d) {
        const c: [number, number] = [Number(e.lngLat.lng.toFixed(7)), Number(e.lngLat.lat.toFixed(7))];
        setDraft({ ...d, coords: d.geometryKind === 'point' ? [c] : [...d.coords, c] });
        return;
      }
      const hit = map.queryRenderedFeatures(e.point, {
        layers: ['assets-points', 'assets-line', 'assets-fill'],
      })[0];
      setSelected((hit?.properties?.id as string | undefined) ?? null);
    });
    for (const l of ['assets-points', 'assets-line', 'assets-fill']) {
      map.on('mouseenter', l, () => (map.getCanvas().style.cursor = 'pointer'));
      map.on('mouseleave', l, () => (map.getCanvas().style.cursor = ''));
    }
    return () => {
      mapRef.current = null;
      setReady(false);
      map.remove();
    };
  }, [theme]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready || !assets.data || !types.data) return;
    setOverlay(map, ASSETS_SOURCE, assetsData(assets.data.items, types.data.items));
    const pts = assets.data.items.flatMap((a) => coordsOf(a.geometry));
    if (pts.length && !selected) {
      const lons = pts.map((p) => p[0]);
      const lats = pts.map((p) => p[1]);
      map.fitBounds(
        [
          [Math.min(...lons), Math.min(...lats)],
          [Math.max(...lons), Math.max(...lats)],
        ],
        { padding: 60, duration: 0, maxZoom: 16 },
      );
    }
  }, [assets.data, types.data, ready]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    const c = draft?.coords ?? [];
    (map.getSource(DRAW) as GeoJSONSource | undefined)?.setData({
      type: 'FeatureCollection',
      features: [
        ...c.map((p) => ({
          type: 'Feature' as const,
          geometry: { type: 'Point' as const, coordinates: p },
          properties: {},
        })),
        ...(c.length > 1
          ? [
              {
                type: 'Feature' as const,
                geometry: {
                  type: 'LineString' as const,
                  coordinates: draft?.geometryKind === 'polygon' ? [...c, c[0]!] : c,
                },
                properties: {},
              },
            ]
          : []),
      ],
    });
    map.getCanvas().style.cursor = draft ? 'crosshair' : '';
  }, [draft, ready]);

  const typeOf = (key: string) => types.data?.items.find((x) => x.key === key);
  const startNew = () => {
    const tt = types.data?.items[0];
    if (!tt || !clientId) return;
    setSelected(null);
    setDraft({
      id: null,
      client_id: clientId,
      type_key: tt.key,
      geometryKind: tt.geometry_kind === 'any' ? 'point' : tt.geometry_kind,
      coords: [],
    });
  };
  return (
    <div className="page">
      <PageHeader
        title={t('assetsPage.title')}
        actions={
          <span className="toolbar">
            <ReportScopePicker withProject={false} />
            {clientId && (
              <a
                className="btn"
                href={`/api/v1/assets?client_id=${clientId}&format=geojson`}
                download="assets.geojson"
              >
                <Download size={15} /> GeoJSON
              </a>
            )}
            {manage && (
              <button type="button" className="btn" onClick={() => setImporting(true)} disabled={!clientId}>
                <Upload size={15} /> {t('assetsPage.import')}
              </button>
            )}
            {manage && (
              <button type="button" className="btn btn-primary" onClick={startNew} disabled={!clientId}>
                <Plus size={15} /> {t('assetsPage.new')}
              </button>
            )}
          </span>
        }
      />
      <div className="split" style={{ gridTemplateColumns: 'minmax(260px, 340px) 1fr' }}>
        <Card title={t('assetsPage.list', { n: assets.data?.total ?? 0 })} bodyClass="">
          <div className="card-body inline-form" style={{ paddingBottom: 0 }}>
            <select
              className="input"
              value={type}
              onChange={(e) => setType(e.target.value)}
              aria-label={t('common.type')}
            >
              <option value="">{t('assetsPage.allTypes')}</option>
              {(types.data?.items ?? []).map((x) => (
                <option key={x.key} value={x.key}>
                  {t(`assetTypes.${x.key}` as 'assetTypes.pole', { defaultValue: x.name_en })}
                </option>
              ))}
            </select>
            <input
              className="input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('common.search')}
              aria-label={t('common.search')}
            />
          </div>
          <div className="ops-scroll" style={{ maxHeight: '58vh' }}>
            <table className="table table-tight table-wrap">
              <tbody>
                {(assets.data?.items ?? []).map((a) => (
                  <tr
                    key={a.id}
                    className={`row-click${selected === a.id ? ' row-selected' : ''}`}
                    onClick={() => (setDraft(null), setSelected(a.id))}
                  >
                    <td>
                      <span
                        className="level-swatch"
                        style={{ background: typeOf(a.type_key)?.color ?? undefined, marginRight: 6 }}
                      />
                      {a.name}
                      <span className="cell-sub">
                        {t(`assetTypes.${a.type_key}` as 'assetTypes.pole', { defaultValue: a.type_key })}
                        {a.code ? ` · ${a.code}` : ''}
                        {a.site_name ? ` · ${a.site_name}` : ''}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`pill${a.status === 'faulty' ? ' pill-critical' : a.status === 'maintenance' ? ' pill-warning' : ''}`}
                      >
                        {t(`assetsPage.status.${a.status}`)}
                      </span>
                    </td>
                  </tr>
                ))}
                {assets.data?.items.length === 0 && (
                  <tr>
                    <td className="muted">{t('assetsPage.none')}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
        <div className="assets-map">
          <div ref={ref} style={{ position: 'absolute', inset: 0 }} />
          {draft && (
            <div className="map-hint">
              <MapPin size={14} />{' '}
              {draft.geometryKind === 'point' ? t('assetsPage.clickPoint') : t('assetsPage.clickVertices')}
            </div>
          )}
        </div>
      </div>
      {(selected || draft) && types.data && (
        <AssetDrawer
          key={selected ?? 'new'}
          assetId={selected}
          draft={draft}
          types={types.data.items}
          onDraft={setDraft}
          onClose={() => (setSelected(null), setDraft(null))}
          onSaved={(id) => {
            setDraft(null);
            setSelected(id);
            void qc.invalidateQueries({ queryKey: ['assets'] });
          }}
          onDeleted={() => {
            setSelected(null);
            void qc.invalidateQueries({ queryKey: ['assets'] });
          }}
          onEditGeometry={(d) => setDraft(d)}
        />
      )}
      {importing && clientId && (
        <ImportModal
          clientId={clientId}
          types={types.data?.items ?? []}
          onClose={() => setImporting(false)}
        />
      )}
    </div>
  );
}

export function coordsOf(g: api.GeoJsonGeometry | null): [number, number][] {
  if (!g) return [];
  const flat = (c: unknown): [number, number][] =>
    Array.isArray(c) && typeof c[0] === 'number'
      ? [c as [number, number]]
      : Array.isArray(c)
        ? c.flatMap(flat)
        : [];
  return flat(g.coordinates);
}

function ImportModal({
  clientId,
  types,
  onClose,
}: {
  clientId: string;
  types: api.AssetType[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [type, setType] = useState(types[0]?.key ?? '');
  const [nameProp, setNameProp] = useState('name');
  const [codeProp, setCodeProp] = useState('');
  const [features, setFeatures] = useState<unknown[] | null>(null);
  const [parseError, setParseError] = useState(false);
  const run = useMutation({
    mutationFn: () =>
      importAssets({
        client_id: clientId,
        type_key: type,
        name_property: nameProp || undefined,
        code_property: codeProp || undefined,
        features: features ?? [],
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['assets'] }),
  });
  return (
    <Modal title={t('assetsPage.import')} onClose={onClose}>
      <p className="muted" style={{ marginTop: 0 }}>
        {t('assetsPage.importHint')}
      </p>
      <div className="form-grid">
        <Field label={t('common.type')}>
          <select className="input" value={type} onChange={(e) => setType(e.target.value)}>
            {types.map((x) => (
              <option key={x.key} value={x.key}>
                {t(`assetTypes.${x.key}` as 'assetTypes.pole', { defaultValue: x.name_en })}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('assetsPage.nameProperty')}>
          <input className="input" value={nameProp} onChange={(e) => setNameProp(e.target.value)} />
        </Field>
        <Field label={t('assetsPage.codeProperty')}>
          <input className="input" value={codeProp} onChange={(e) => setCodeProp(e.target.value)} />
        </Field>
        <Field label={t('assetsPage.file')}>
          <input
            className="input"
            type="file"
            accept=".geojson,.json,application/geo+json,application/json"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              try {
                const j = JSON.parse(await f.text()) as { features?: unknown[] };
                setFeatures(Array.isArray(j.features) ? j.features : null);
                setParseError(!Array.isArray(j.features));
              } catch {
                setParseError(true);
                setFeatures(null);
              }
            }}
          />
        </Field>
      </div>
      {parseError && <p className="form-error">{t('assetsPage.badFile')}</p>}
      {features && <p className="muted">{t('assetsPage.featuresFound', { n: features.length })}</p>}
      {run.data && (
        <p className="form-ok">
          {t('assetsPage.imported', { created: run.data.created, skipped: run.data.skipped })}
          {run.data.errors.slice(0, 5).map((e) => (
            <span key={e} className="cell-sub">
              {e}
            </span>
          ))}
        </p>
      )}
      <div className="form-actions">
        <button type="button" className="btn" onClick={onClose}>
          {t('common.close')}
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={!features?.length || run.isPending}
          onClick={() => run.mutate()}
        >
          {t('assetsPage.import')}
        </button>
      </div>
    </Modal>
  );
}
